import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Upload,
  Button,
  message,
  useClassNames,
  Form,
  Text,
  StatusTip,
  Icon,
} from '@tencent/tea-component';
import { uploadToCos, base64ToFile } from 'src/utils/upload-cos';
import CropperModal from '../CropperModal';
import classNames from 'classnames';
import { Status } from '@tencent/calendar-framework-wemeet-upload';
import axios, { CancelTokenSource } from 'axios';
import AnalysisPrimaryColor, { rgbToHex } from 'src/utils/color-thief/index';
import ImgPreview, { ImagePreviewRef } from '@component/ImageUpload/Preview';
import './index.scss';
const { CancelToken } = axios;
export interface IProps {
  uploadMsg?: string; // 底部提示文案
  maxSize?: number; // 最大上传
  minWidth?: number; // 上传图片的最小宽度
  minHeight?: number; // 上传图片的最小高度
  aspectRatio?: number;
  image?: string; // 传值表示已上传图片
  imageName?: string; // 表示已上传图片名字
  imageClassName?: string; // 传入image的样式
  isDeaultImage?: boolean; // 是否为默认图片
  uploadBtnsIn?: boolean; // 下载按钮在里面
  showPreviewButton?: boolean; // 是否有预览功能
  isPreview?: boolean; // 是否预览
  imageFeature?: string; // 上传地址
  accept?: string; // 上传文件类型
  style?: any; // 当前传入样式
  confirm?: (cosData: any) => void;
  updateMainColor?: (color: any) => void;
  onChangeFileStatus?: (cosData: any) => void; // 图片文件修改回调状态传递
  onDelete?: () => void; // 图片删除回调
  onCancel?: () => void; // 图片取消上传回调
  onProgress?: () => void; // 上传图片进度
}

// 裁剪上传组件
const CropperUpload = (props: IProps) => {
  const {
    uploadMsg = '', // 提示文案
    maxSize = 5 * 1024 * 1024, // 上传文件最大值
    minWidth = 0,
    minHeight = 0,
    aspectRatio = 16 / 9, // 裁剪比例
    image = '', // 已上传的图片
    imageName = '',
    imageClassName = '',
    isDeaultImage = false,
    uploadBtnsIn = false,
    showPreviewButton = true,
    isPreview = true,
    imageFeature = 'activity_image',
    accept = 'image/png,image/jpeg,image/jpg',
    style,
    confirm, // 裁剪上传完成回调
    updateMainColor, // 主色识别完成回调
    onChangeFileStatus,
    onDelete,
    onCancel,
    onProgress,
  } = props;
  const [modalVisible, setModalVisible] = useState(false); // 裁剪弹窗显示
  const [selectFile, setSelectFile] = useState({} as any); // 选择的文件
  const [fileUrl, setFileUrl] = useState(''); // 传给裁剪组件的本地fileurl
  const [fileName, setFileName] = useState(''); // filename 属性不为空时才会渲染文件信息
  const [imageUrl, setImageUrl] = useState(''); // 预览图
  const [uploadStatus, setUploadStatus] = useState<any>(null); // 上传状态
  const [colorStatus, setColorStatus] = useState(false);
  const analysisColor = new AnalysisPrimaryColor();

  const { Margin: margin } = useClassNames();
  // 用于取消上传使用
  const [tokenSource, setTokenSource] = useState<CancelTokenSource>(CancelToken.source());

  // 当前展示状态和名字
  const showFileName = useMemo(() => {
    if (uploadStatus === Status.success) {
      return (
        <>
          <Icon type="success" />
          {fileName || '文件名称'}
        </>
      );
    }
    if (uploadStatus === Status.loading) {
      return <StatusTip loadingText="上传中" status="loading" />;
    }
    if (uploadStatus === Status.error) {
      return <StatusTip errorText="上传失败" status="error" />;
    }
    if (uploadStatus === Status.cancel) {
      return <StatusTip errorText="取消上传" status="error" />;
    }
    if (isDeaultImage) return '';
    return fileName;
  }, [fileName, uploadStatus, isDeaultImage]);
  useEffect(() => {
    setFileName(imageName || image); // 取消修改后,filename设为初始值
    setImageUrl(image); // 已上传图片，直接填入
  }, [image]);

  const getImgWidthHeight = (url: string) => {
    const img = new Image();
    img.src = url;
    return new Promise((resolve, reject) => {
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height,
        });
      };
      img.onerror = (err) => {
        reject(err);
      };
    });
  };

  // 上传前触发
  const handleBefore = (file: File, fileList: Array<File>, accept: boolean) => {
    if (!accept) {
      if (fileList.length >= 2) {
        message.error({
          content: '不支持单次上传多张图片',
          className: 'is-align-middle',
        });
        return false;
      }
      message.error({
        content: '请上传5M以下的png/jpg/jpeg图片',
        className: 'is-align-middle',
      });
      return false;
    }
    const windowURL = window.URL || window.webkitURL; // 兼容操作
    const dataURl = windowURL.createObjectURL(file);
    getImgWidthHeight(dataURl).then(
      (imgData: any) => {
        const { width, height } = imgData;
        if (width < minWidth || height < minHeight) {
          message.error({
            content: '分辨率过小，请重新上传',
            className: 'is-align-middle',
          });
        } else {
          setModalVisible(true);
          setFileUrl(dataURl);
          setSelectFile(file);
          setUploadStatus(null);
        }
      },
      () => {
        message.error({
          content: '请重新上传',
          className: 'is-align-middle',
        });
      },
    );
    return true;
  };

  // 裁剪完成，点击确认
  const handleModalConfirm = (str: string) => {
    setModalVisible(false);
    const newFile = base64ToFile(str, selectFile.name);
    // 存在上传主色回调时才进行主色识别
    if (updateMainColor) setColorStatus(true);
    setUploadStatus(Status.loading);
    // 当前调用图片上传状态逻辑
    onChangeFileStatus?.({
      url: '',
      cosId: '',
      name: selectFile.name,
      status: Status.loading,
      localUrl: '',
    });
    // 开始上传图片
    uploadToCos(
      newFile,
      isPreview,
      () => {
        onProgress?.();
      },
      tokenSource.token,
      imageFeature,
    ).then(
      (cosData: any) => {
        setTokenSource(CancelToken.source());
        setImageUrl(str);
        setFileName(selectFile.name);
        setUploadStatus(Status.success);
        // 上传成功状态回调
        onChangeFileStatus?.({
          url: cosData.cosUrl || str,
          cosId: cosData.cosId,
          name: selectFile.name,
          status: Status.success,
          localUrl: URL.createObjectURL(selectFile),
        });
        // 上传成功的回调
        confirm?.({
          url: cosData.cosUrl || str,
          cosId: cosData.cosId,
        });
      },
      (err) => {
        if (err?.message === 'cancel') {
          setUploadStatus(Status.cancel);
          // 取消上传状态回调
          onChangeFileStatus?.({
            url: imageUrl,
            cosId: '',
            name: selectFile.name,
            status: Status.cancel,
            localUrl: imageUrl,
          });
          return;
        }
        if (typeof err === 'string') {
          message.error({ content: err });
        } else {
          message.error({ content: '上传失败，请检查您的网络' });
        }
        setUploadStatus(Status.error);
      },
    );
  };

  // 取消裁剪上传
  const handleModalCancel = () => {
    setModalVisible(false);
  };

  // 删除文件
  const handleDelete = useCallback(() => {
    setSelectFile({});
    setFileName('');
    setImageUrl('');
    setColorStatus(false);
    setUploadStatus(null);
    // 删除图片回调
    onDelete?.();
    confirm?.({
      url: '',
      cosId: '',
    });
  }, [confirm, imageUrl]);

  const imgRef = React.useRef<HTMLDivElement>(null);
  const previewRef = React.useRef<ImagePreviewRef>(null);

  useEffect(() => {
    if (imgRef.current && previewRef.current) {
      previewRef.current.patchElement(imgRef.current);
    }
  }, [imgRef.current, previewRef.current]);

  const handleOpenPreview = () => {
    if (previewRef.current) {
      previewRef.current.open();
    }
  };
  // 取消上传
  const handleCancel = useCallback(() => {
    if (tokenSource) tokenSource.cancel('cancel');
    onCancel?.();
  }, [tokenSource, onCancel]);

  const renderImage = ({ open, isDragging }: any) => {
    if (imageUrl || uploadBtnsIn) {
      return (
        <Upload.Image
          style={{ width: '100%' }}
          filename={showFileName}
          image={imageUrl}
          ref={imgRef}
          className={`uploadImg ${imageClassName}`}
          button={
            <>
              {isDeaultImage && uploadBtnsIn && uploadStatus !== Status.loading && (
                <Button type="primary" className=" size-xs" onClick={open}>
                  上传图片
                </Button>
              )}
              {!isDeaultImage && uploadStatus !== Status.loading && (
                <Button type="primary" className=" size-xs" onClick={open}>
                  重新上传
                </Button>
              )}
              {uploadStatus === Status.loading && (
                <Button type="primary" className=" size-xs" onClick={handleCancel}>
                  取消上传
                </Button>
              )}
              {!isDeaultImage && showPreviewButton && (
                <Button type="link" className="size-xs" onClick={handleOpenPreview}>
                  预览
                </Button>
              )}
              {!isDeaultImage && uploadStatus === Status.success && (
                <Button type="link" className="size-xs" onClick={handleDelete}>
                  删除
                </Button>
              )}
            </>
          }
        />
      );
    }
    return (
      <Upload.Dragger filename={showFileName} image={imageUrl}>
        {isDragging ? '释放鼠标' : <Text theme="weak">点击“上传”按钮或将文件拖拽到此区域</Text>}
      </Upload.Dragger>
    );
  };
  // 当前上传按钮在虚线里面
  if (uploadBtnsIn) {
    <Form.Control message={uploadMsg} className="none-pb cropperFormControl" style={style}>
      <div className="ma-t-form-unit">
        <div className={classNames(margin.Top['5n'], 'ma-t-form-unit ma-t-form-unit--block')}>
          <Upload beforeUpload={handleBefore} maxSize={maxSize} accept={accept}>
            {(params: any) => renderImage(params)}
          </Upload>
        </div>
        <CropperModal
          showModal={modalVisible}
          aspectRatio={aspectRatio}
          onConfirm={handleModalConfirm}
          onCancel={handleModalCancel}
          imgUrl={fileUrl}
        />
      </div>
      {showPreviewButton && <ImgPreview url={imageUrl} ref={previewRef}></ImgPreview>}
    </Form.Control>;
  }
  return (
    <Form.Control message={uploadMsg} className="none-pb cropperFormControl" style={style}>
      <div className="ma-t-form-unit">
        {!imageUrl && !uploadBtnsIn && (
          <div className="ma-t-form-unit ma-t-form-unit--block">
            <Upload beforeUpload={handleBefore} maxSize={maxSize} accept={accept}>
              <Button type="primary" className="size-xs">
                <i className={classNames('ma-t-icon ma-t-icon-upload-white', margin.Right['1n'])} />
                上传文件
              </Button>
            </Upload>
          </div>
        )}
        <div
          className={classNames(
            { [margin.Top['5n']]: !imageUrl },
            'ma-t-form-unit ma-t-form-unit--block',
          )}
        >
          <Upload beforeUpload={handleBefore} maxSize={maxSize} accept={accept}>
            {(params: any) => renderImage(params)}
          </Upload>
        </div>
        <CropperModal
          showModal={modalVisible}
          aspectRatio={aspectRatio}
          onConfirm={handleModalConfirm}
          onCancel={handleModalCancel}
          imgUrl={fileUrl}
        />
      </div>
      <img
        src={imageUrl}
        onLoad={(e) => {
          if (colorStatus) {
            setColorStatus(false);
            const [a, b, c] = analysisColor.getColor(e.target);
            const color = rgbToHex(a, b, c);
            console.log(a, b, c, color);
            updateMainColor?.(color);
          }
        }}
        alt=""
        style={{ display: 'none' }}
      />
      {showPreviewButton && <ImgPreview url={imageUrl} ref={previewRef}></ImgPreview>}
    </Form.Control>
  );
};

export default CropperUpload;
